export const FAMILY_RELATIONSHIPS = new Set([
  "spouse", "partner", "child", "step-child", "grandchild", "parent",
  "sibling", "niece-nephew", "grandparent", "aunt-uncle", "cousin", "other-family",
]);

export const Relationship = {
  Spouse: "spouse",
  Partner: "partner",
  FormerSpouse: "former-spouse",
  Child: "child",
  StepChild: "step-child",
  Grandchild: "grandchild",
  Parent: "parent",
  Sibling: "sibling",
  NieceNephew: "niece-nephew",
  Grandparent: "grandparent",
  AuntUncle: "aunt-uncle",
  Cousin: "cousin",
  OtherFamily: "other-family",
  Friend: "friend",
  Other: "other",
  Attorney: "attorney",
  OtherProfessional: "other-professional",
} as const;

export type RelationshipValue = (typeof Relationship)[keyof typeof Relationship];

export const RELATIONSHIP_OPTIONS: {
  value: RelationshipValue;
  label: string;
  description?: string;
}[] = [
  {
    value: Relationship.Spouse,
    label: "Spouse",
    description: "Married at the time of death. Marriages from other states and countries count.",
  },
  {
    value: Relationship.Partner,
    label: "Partner",
    description:
      "In a committed relationship, but not married. The court treats this differently from a spouse, so the label matters.",
  },
  {
    value: Relationship.FormerSpouse,
    label: "Former spouse",
    description:
      "Divorced. The divorce affects what the court expects, so this is worth marking even if the will is older.",
  },
  {
    value: Relationship.Child,
    label: "Child",
    description: "Adopted or biological. The court treats both the same.",
  },
  {
    value: Relationship.StepChild,
    label: "Step-child",
    description:
      "A spouse's child not legally adopted. The court treats step-children differently from adopted children, so the distinction matters.",
  },
  {
    value: Relationship.Grandchild,
    label: "Grandchild",
  },
  {
    value: Relationship.Parent,
    label: "Parent",
  },
  {
    value: Relationship.Sibling,
    label: "Sibling",
    description: "A brother or sister, including half-siblings.",
  },
  {
    value: Relationship.NieceNephew,
    label: "Niece or nephew",
    description: "A child of a sibling.",
  },
  {
    value: Relationship.Grandparent,
    label: "Grandparent",
  },
  {
    value: Relationship.AuntUncle,
    label: "Aunt or uncle",
  },
  {
    value: Relationship.Cousin,
    label: "Cousin",
  },
  {
    value: Relationship.OtherFamily,
    label: "Other family",
    description: "Related in a way not listed here.",
  },
  {
    value: Relationship.Friend,
    label: "Friend",
    description:
      "Not related, but part of the picture: named in the will, close to the family, or helping settle the estate.",
  },
  {
    value: Relationship.Other,
    label: "Other",
    description: "Not related or not sure how to classify this person.",
  },
];

export const ONBOARDING_RELATIONSHIP_OPTIONS = [
  ...RELATIONSHIP_OPTIONS,
  { value: Relationship.Attorney, label: "Attorney" },
  { value: Relationship.OtherProfessional, label: "Other professional" },
] as const;

const LEGACY_VALUE_MAP: Record<string, RelationshipValue> = {
  niece: Relationship.NieceNephew,
  nephew: Relationship.NieceNephew,
  uncle: Relationship.AuntUncle,
  aunt: Relationship.AuntUncle,
};

export function normalizeStoredRelationship(value: string | null): string | null {
  if (!value) return value;
  return LEGACY_VALUE_MAP[value] ?? value;
}

export function getRelationshipLabel(value: string): string {
  const normalized = LEGACY_VALUE_MAP[value] ?? value;
  const option = ONBOARDING_RELATIONSHIP_OPTIONS.find((o) => o.value === normalized);
  return option ? option.label : value.charAt(0).toUpperCase() + value.slice(1);
}
